import {
	type AnalyticsEventPayload,
	type AiSuggestionInteractionPoint,
	type AiSuggestionsEntryPoint,
	type AiSuggestionsEventPayload,
	type EditorAnalyticsAPI,
	EVENT_TYPE,
	ACTION,
	ACTION_SUBJECT,
} from '@atlaskit/editor-common/analytics';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';

import type { Suggestion, ToolCall } from '../../aiSuggestionsPluginType';

type AdfNode = {
	attrs?: { localId?: unknown };
	content?: unknown;
	type?: unknown;
};

// Walks an inserted ADF subtree to collect every `localId` and node `type`. Captures localIds at
// every depth because tool-call collisions can be triggered by nested ids, not just the root.
function collectFromAdf(node: AdfNode | undefined, localIds: Set<string>, nodeTypes: Set<string>) {
	if (!node || typeof node !== 'object') {
		return;
	}
	if (typeof node.type === 'string') {
		nodeTypes.add(node.type);
	}
	const localId = node.attrs?.localId;
	if (typeof localId === 'string') {
		localIds.add(localId);
	}
	if (Array.isArray(node.content)) {
		for (const child of node.content) {
			collectFromAdf(child as AdfNode, localIds, nodeTypes);
		}
	}
}

function collectFromToolCall(toolCall: ToolCall, localIds: Set<string>, nodeTypes: Set<string>) {
	if (toolCall.name === 'removeNodes') {
		for (const id of toolCall.arguments.localIds) {
			localIds.add(id);
		}
		return;
	}
	localIds.add(toolCall.arguments.localId);
	if ('value' in toolCall.arguments && typeof toolCall.arguments.value === 'string') {
		try {
			collectFromAdf(JSON.parse(toolCall.arguments.value) as AdfNode, localIds, nodeTypes);
		} catch {
			// Ignore malformed JSON - the tool call would also fail to apply, surfaced elsewhere.
		}
	}
}

function collectToolCallAttributes(toolCall: ToolCall) {
	const localIds = new Set<string>();
	const nodeTypes = new Set<string>();
	collectFromToolCall(toolCall, localIds, nodeTypes);
	return {
		name: toolCall.name,
		localIds: Array.from(localIds),
		nodeTypes: Array.from(nodeTypes),
	};
}

/**
 * Attach analytics when applying a suggestion's tool calls produces no semantic doc change.
 * Surfaces silent "no diff" cases (duplicate-localId collisions, unresolvable targets, self-
 * cancelling step pairs). Caller decides when to invoke based on `appliedTr.before.eq(appliedTr.doc)`.
 */
export const addNoDiffAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(transaction: Transaction, suggestion: Suggestion): Transaction => {
		const toolCalls = suggestion.toolCalls.map(collectToolCallAttributes);

		const payload = {
			action: ACTION.NO_DIFF_FOUND,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: {
				suggestionType: suggestion.type,
				toolCalls,
			},
		} as AnalyticsEventPayload;
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Returns the number of unique ADF blocks affected by a suggestion.
 */
export const getAffectedBlocks = (suggestion: Suggestion): number => {
	const ids = new Set<string>();
	for (const toolCall of suggestion.toolCalls) {
		if (toolCall.name === 'removeNodes') {
			for (const id of toolCall.arguments.localIds) {
				ids.add(id);
			}
		} else {
			ids.add(toolCall.arguments.localId);
		}
	}
	return ids.size;
};

/**
 * Returns the total plain text characters being added by a suggestion.
 * Derived from the `value` ADF JSON of inserting/replacing tool calls.
 */
function getTextFromAdfNode(node: AdfNode & { text?: string }): string {
	if (node.type === 'text' && typeof node.text === 'string') {
		return node.text;
	}
	if (Array.isArray(node.content)) {
		return node.content.map((child) => getTextFromAdfNode(child as AdfNode)).join('');
	}
	return '';
}

export const getCharactersAdded = (suggestion: Suggestion): number => {
	let count = 0;
	for (const toolCall of suggestion.toolCalls) {
		if ('value' in toolCall.arguments && typeof toolCall.arguments.value === 'string') {
			try {
				count += getTextFromAdfNode(JSON.parse(toolCall.arguments.value) as AdfNode).length;
			} catch {
				// malformed JSON — skip
			}
		}
	}
	return count;
};

/**
 * Returns the total plain text characters being removed by a suggestion.
 * Must be called before applySuggestion so the original doc content is intact.
 * Uses ProseMirror's built-in node.textContent for accurate plain text extraction.
 */
export const getCharactersRemoved = (suggestion: Suggestion, doc: PMNode): number => {
	const removingCalls = ['removeNode', 'removeNodes', 'replaceNode'];
	const visitedIds = new Set<string>();
	let count = 0;
	for (const toolCall of suggestion.toolCalls) {
		if (!removingCalls.includes(toolCall.name)) {
			continue;
		}
		const localIds =
			toolCall.name === 'removeNodes' ? toolCall.arguments.localIds : [toolCall.arguments.localId];
		for (const localId of localIds) {
			if (visitedIds.has(localId)) {
				continue;
			}
			visitedIds.add(localId);
			doc.descendants((node) => {
				if (node.attrs.localId === localId) {
					count += node.textContent.length;
					return false;
				}
			});
		}
	}
	return count;
};

/**
 * Attach analytics when a user clicks an entry point that starts suggested edits.
 */
export const addEntryPointClickedAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(transaction: Transaction, entryPoint: AiSuggestionsEntryPoint): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				entryPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Attach analytics when a suggestion is discarded/rejected.
 * Mirrors addAcceptSuggestionAnalytics but for the discard case.
 * No doc-content measurements are needed since no change is applied to the document.
 */
export const addDiscardSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
	): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.DISCARDED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				affectedBlocks: getAffectedBlocks(suggestion),
				suggestionType: suggestion.type,
				interactionPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Returns the block-level ADF node types affected by a suggestion.
 * Uses the ProseMirror schema (via doc.type.schema) to determine block-ness
 */
export const getBlockTypes = (suggestion: Suggestion, doc: PMNode): string[] => {
	const localIds = new Set<string>();
	const nodeTypes = new Set<string>();
	for (const toolCall of suggestion.toolCalls) {
		collectFromToolCall(toolCall, localIds, nodeTypes);
	}

	// Look up removed nodes in the doc by localId to capture their types (removeNodes has no ADF value).
	doc.descendants((node) => {
		if (typeof node.attrs?.localId === 'string' && localIds.has(node.attrs.localId)) {
			nodeTypes.add(node.type.name);
		}
	});

	const schema = doc.type.schema;
	return Array.from(nodeTypes).filter((typeName) => schema.nodes[typeName]?.isBlock === true);
};

/**
 * Attach analytics when a suggestion is viewed/opened.
 * Fires when the user explicitly opens a suggestion — either by clicking the
 * sparkle icon button in the editor or by expanding a suggestion in the comments panel list.
 * Must be called inside core.actions.execute before any doc mutations so
 * tr.doc still has the original content for computing charactersToRemove.
 */
export const addViewSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
	): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.VIEWED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				affectedBlocks: getAffectedBlocks(suggestion),
				blockTypes: getBlockTypes(suggestion, transaction.doc),
				charactersToAdd: getCharactersAdded(suggestion),
				charactersToRemove: getCharactersRemoved(suggestion, transaction.doc),
				interactionPoint,
				suggestionCardCharacterCount: suggestion.shortDescription?.length ?? 0,
				suggestionType: suggestion.type,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Attach analytics when a suggestion is accepted.
 * Must be called inside core.actions.execute before applySuggestion,
 * so tr.doc still has the original content for computing charactersRemoved.
 */
export const addAcceptSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
	): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.ACCEPTED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				affectedBlocks: getAffectedBlocks(suggestion),
				charactersAdded: getCharactersAdded(suggestion),
				charactersRemoved: getCharactersRemoved(suggestion, transaction.doc),
				suggestionType: suggestion.type,
				interactionPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};
